import * as React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { yellow } from '@mui/material/colors';

export enum GaugeValue {
  Green = "Green",
  Yellow = "Yellow",
  Red = "Red"
}

export interface GaugeData {
  name: string;
  value: GaugeValue
}

interface GaugeChartProps {
  value: GaugeValue;
  width?: string | number;
}

const GaugeChart: React.FC<GaugeChartProps> = ({ value, width }) => {
  const theme = useTheme();

  const viewBoxSize = 200;
  const cx = viewBoxSize / 2;
  const stroke = 14;
  const strokeHalf = stroke / 2;
  const margin = 2;
  const cy = (viewBoxSize / 2) - (strokeHalf + margin);
  const radius = Math.min(cx - 12, cy - (strokeHalf + margin));

  const redAngle = 30;
  const yellowAngle = 90;
  const greenAngle = 150;

  let angle = redAngle;

  if (value == GaugeValue.Yellow) {
    angle = yellowAngle;
  } else if (value == GaugeValue.Green) {
    angle = greenAngle;
  }

  const theta = (Math.PI * (180 - angle)) / 180;

  const needleLength = radius - 12;
  const needleTipX = cx + needleLength * Math.cos(theta);
  const needleTipY = cy - needleLength * Math.sin(theta);

  const arcPath = (
    startDeg: number,
    endDeg: number,
    color: string,
    opts?: { width?: number; opacity?: number; filter?: string; key?: string }
  ) => {
    const startRad = (Math.PI * startDeg) / 180;
    const endRad = (Math.PI * endDeg) / 180;

    const x1 = cx + radius * Math.cos(Math.PI - startRad);
    const y1 = cy - radius * Math.sin(Math.PI - startRad);
    const x2 = cx + radius * Math.cos(Math.PI - endRad);
    const y2 = cy - radius * Math.sin(Math.PI - endRad);

    const largeArc = endDeg - startDeg > 180 ? 1 : 0;

    return (
      <path
        key={`${startDeg}-${endDeg}${opts?.key ? `-${opts.key}` : ''}`}
        d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`}
        fill="none"
        stroke={color}
        strokeWidth={opts?.width ?? stroke}
        strokeOpacity={opts?.opacity}
        filter={opts?.filter}
        strokeLinecap="butt"
      />
    );
  };
  const paletteRed = theme.palette.error.main;
  const paletteYellow = yellow[500];
  const paletteGreen = theme.palette.success.main;
  const trackColor = theme.palette.divider;
  const needleColor = theme.palette.text.primary;

  return (
    <Box sx={{ width, aspectRatio: '2 / 1' }}>
      <svg
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize / 2}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%' }}
        role="img"
        aria-label={`Gauge showing status ${value}`}
      >
        <defs>
          <filter id="selGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="0.8" floodOpacity="0.18" />
          </filter>
        </defs>

        {arcPath(0, 180, trackColor)}

        {(() => {
          const selected = value;
          const selectedWidth = stroke + 1;
          const fade = 0.55;
          return (
            <g>
              {arcPath(0, 60, paletteRed, { opacity: selected === GaugeValue.Red ? 0.95 : fade })}
              {selected === GaugeValue.Red && arcPath(0, 60, paletteRed, { key: 'sel', width: selectedWidth, opacity: 1, filter: 'url(#selGlow)' })}
              {arcPath(60, 120, paletteYellow, { opacity: selected === GaugeValue.Yellow ? 0.95 : fade })}
              {selected === GaugeValue.Yellow && arcPath(60, 120, paletteYellow, { key: 'sel', width: selectedWidth, opacity: 1, filter: 'url(#selGlow)' })}
              {arcPath(120, 180, paletteGreen, { opacity: selected === GaugeValue.Green ? 0.95 : fade })}
              {selected === GaugeValue.Green && arcPath(120, 180, paletteGreen, { key: 'sel', width: selectedWidth, opacity: 1, filter: 'url(#selGlow)' })}
            </g>
          );
        })()}

        <line
          x1={cx}
          y1={cy}
          x2={needleTipX}
          y2={needleTipY}
          stroke={needleColor}
          strokeWidth={3}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={5} fill={needleColor} />
        <circle cx={cx} cy={cy} r={3} fill={theme.palette.background.paper} />
      </svg>
    </Box>
  );
};
export default GaugeChart;
